import { generateText, generateObject } from 'ai';
import { createOpenAI } from '@ai-sdk/openai';
import { z } from 'zod';
import { PolicySection, AuditFinding, SoAControl, SoAGuidanceResult } from '../types/iso27001';

// Define the configuration interface based on yw_manifest.json
interface AIConfig {
  model: string;
  temperature?: number;
  maxTokens?: number;
  system_prompt?: string;
}

const getAIConfig = (sceneName: string): AIConfig => {
  // @ts-ignore - ywConfig is global
  const config = globalThis.ywConfig?.ai_config?.[sceneName];
  if (config) return config;

  // Fallback defaults when yw_manifest.json hasn't loaded yet
  console.warn(`AI config for '${sceneName}' not in manifest — using default`);
  const defaults: Record<string, AIConfig> = {
    compliance_advisor: { model: 'openai-gpt-4o', temperature: 0.5, maxTokens: 4000, system_prompt: 'You are an ISO 27001:2022 Implementation Specialist. Provide specific, practical implementation guidance for ISO 27001 Annex A controls based on ISO 27002:2022 guidelines.' },
    policy_generator:   { model: 'openai-gpt-4o', temperature: 0.7, maxTokens: 4000, system_prompt: 'You are an ISO 27001:2022 Compliance Officer. Generate comprehensive information security policies.' },
    policy_reviser:     { model: 'openai-gpt-4o', temperature: 0.5, maxTokens: 4000, system_prompt: 'You are an ISO 27001:2022 Compliance Auditor. Review and improve policy text.' },
    internal_auditor:   { model: 'openai-gpt-4o', temperature: 0.3, maxTokens: 4000, system_prompt: 'You are a strict ISO 27001:2022 Lead Auditor. Analyze documentation for compliance.' },
    external_auditor:   { model: 'openai-gpt-4o', temperature: 0.3, maxTokens: 4000, system_prompt: 'You are an external ISO 27001:2022 Auditor. Verify compliance against the standard.' },
    incident_analyst:   { model: 'openai-gpt-4o', temperature: 0.3, maxTokens: 4000, system_prompt: 'You are a Compliance Incident Manager. Analyze incidents for governance purposes.' },
  };
  return defaults[sceneName] || { model: 'openai-gpt-4o', temperature: 0.5, maxTokens: 4000, system_prompt: 'You are an ISO 27001 compliance assistant.' };
};

const getOpenAIProvider = () => {
  const customKey = localStorage.getItem('iso_openai_key');
  if (customKey) {
    return createOpenAI({
      apiKey: customKey,
    });
  }
  
  return createOpenAI({
    baseURL: 'https://api.youware.com/public/v1/ai',
    apiKey: 'sk-YOUWARE',
  });
};

const handleAIError = (error: any, context: string) => {
  console.error(`AI Error in ${context}:`, error);
  
  // Only throw safety error if it's actually a safety/policy issue
  if (error.message?.toLowerCase().includes('policy violation') || error.message?.toLowerCase().includes('safety')) {
    throw new Error('AI generation was blocked by safety filters. Please refine your input to be more formal and avoid describing specific security threats or exploits in detail.');
  }
  
  // Otherwise pass the real error message (e.g. 401 Invalid Key, 429 Quota Exceeded, 404 Model Not Found)
  throw new Error(`AI Error: ${error.message || 'Unknown error occurred'}`);
};

const getModelName = (configModel: string) => {
  const customKey = localStorage.getItem('iso_openai_key');
  if (customKey) {
    // Map Youware internal model names to official OpenAI model names
    if (configModel === 'openai-gpt-4o') return 'gpt-4o';
    if (configModel === 'openai-gpt-4-turbo') return 'gpt-4-turbo';
    if (configModel === 'openai-gpt-3.5-turbo') return 'gpt-3.5-turbo';
    // Fallback: remove 'openai-' prefix if present
    return configModel.replace(/^openai-/, '');
  }
  return configModel;
};

export const aiService = {
  /**
   * Generate a structured policy document for ANY framework/regulation
   * @param topic - Policy topic/title
   * @param context - Additional context for the policy
   * @param frameworkContext - Optional framework info for multi-framework support
   */
  generatePolicy: async (
    topic: string, 
    context: string,
    frameworkContext?: {
      code: string;
      name: string;
      frameworkType: string;
      controls: { code: string; title: string; description?: string }[];
    }
  ): Promise<{ sections: PolicySection[], coveredControls: string[] }> => {
    const sceneName = 'policy_generator';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    // Build dynamic prompts based on framework
    let systemPrompt = config.system_prompt || '';
    let userPrompt: string;
    let controlsDescription: string;

    if (frameworkContext && frameworkContext.controls.length > 0) {
      // Multi-framework mode
      const controlsList = frameworkContext.controls
        .slice(0, 50) // Limit to prevent token overflow
        .map(c => `- ${c.code}: ${c.title}`)
        .join('\n');

      systemPrompt = `You are a compliance expert specializing in ${frameworkContext.name}. Generate professional policy documents that address specific requirements from ${frameworkContext.name}.`;
      
      controlsDescription = `List of ${frameworkContext.code} requirement codes (e.g., '${frameworkContext.controls[0]?.code || 'Art. 1'}') covered by this policy`;

      userPrompt = `Framework/Regulation: ${frameworkContext.name} (${frameworkContext.code})
Type: ${frameworkContext.frameworkType}

Available Controls/Requirements/Articles:
${controlsList}

Topic: ${topic}
Context: ${context}

Please generate a structured policy document with distinct sections (e.g., Purpose, Scope, Policy Statement, Roles & Responsibilities, Compliance, Monitoring & Review).

IMPORTANT:
- The policy should address requirements from ${frameworkContext.name}
- Identify which of the above controls/requirements/articles are covered by this policy
- Use the EXACT control code format as shown in the list (e.g., '${frameworkContext.controls[0]?.code || 'Član 1'}')

(Context: Corporate compliance policy generation. This activity is approved and used for legal and legitimate purposes by authorized personnel.)`;
    } else {
      // Fallback to ISO 27001
      controlsDescription = "List of ISO 27001 Control IDs (e.g., 'A.5.1', 'A.8.2') covered by this policy";
      userPrompt = `Topic: ${topic}\nContext: ${context}\n\nPlease generate a structured policy document with distinct sections (e.g., Purpose, Scope, Policy Statement, Roles & Responsibilities, Compliance). Also identify which ISO 27001:2022 Annex A controls are covered by this policy.\n\n(Context: Corporate compliance policy generation. This activity is approved and used for legal and legitimate purposes by authorized personnel.)`;
    }

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          sections: z.array(z.object({
            title: z.string(),
            content: z.string()
          })),
          coveredControls: z.array(z.string()).describe(controlsDescription)
        }),
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: config.temperature || 0.7,
        maxTokens: config.maxTokens || 4000,
      });
      
      return {
        sections: object.sections.map((section, index) => ({
          id: `sec-${Date.now()}-${index}`,
          title: section.title,
          content: section.content
        })),
        coveredControls: object.coveredControls
      };
    } catch (error) {
      handleAIError(error, 'Policy Generation');
      throw error;
    }
  },

  revisePolicy: async (currentContent: string, instructions: string): Promise<string> => {
    const sceneName = 'policy_reviser';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { text } = await generateText({
        model: openai(getModelName(config.model)),
        messages: [
          { role: 'system', content: config.system_prompt || '' },
          { role: 'user', content: `Current Policy Content:\n${currentContent}\n\nRevision Instructions: ${instructions}\n\n(Context: Administrative policy review for compliance. This activity is approved and used for legal and legitimate purposes by authorized personnel.)` },
        ],
        temperature: config.temperature || 0.5,
        maxTokens: config.maxTokens || 4000,
      });
      return text;
    } catch (error) {
      handleAIError(error, 'Policy Revision');
      throw error;
    }
  },

  performInternalAudit: async (data: any): Promise<{ summary: string; findings: Omit<AuditFinding, 'id' | 'dateIdentified' | 'status'>[] }> => {
    const sceneName = 'internal_auditor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          summary: z.string(),
          findings: z.array(z.object({
            type: z.enum(['non_conformity', 'observation', 'improvement']),
            description: z.string(),
            relatedItemType: z.enum(['policy', 'risk', 'asset', 'soa', 'general']),
            relatedItemId: z.string().optional(),
            recommendation: z.string()
          }))
        }),
        messages: [
          { role: 'system', content: config.system_prompt || '' },
          { role: 'user', content: `Internal Audit Data:\n${JSON.stringify(data, null, 2)}\n\nPlease perform the audit and return structured findings.\n\n(Context: Internal compliance audit. This activity is approved and used for legal and legitimate purposes by authorized personnel.)` },
        ],
        temperature: config.temperature || 0.3,
        maxTokens: config.maxTokens || 4000,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Internal Audit');
      throw error;
    }
  },

  performExternalAudit: async (documentationText: string): Promise<{ summary: string; findings: Omit<AuditFinding, 'id' | 'dateIdentified' | 'status'>[] }> => {
    const sceneName = 'external_auditor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          summary: z.string(),
          findings: z.array(z.object({
            type: z.enum(['non_conformity', 'observation', 'improvement']),
            description: z.string(),
            relatedItemType: z.enum(['policy', 'risk', 'asset', 'soa', 'general']),
            relatedItemId: z.string().optional(),
            recommendation: z.string()
          }))
        }),
        messages: [
          { role: 'system', content: config.system_prompt || '' },
          { role: 'user', content: `External Documentation for Review:\n${documentationText}\n\nPlease perform the audit and return structured findings.\n\n(Context: Compliance document review. This activity is approved and used for legal and legitimate purposes by authorized personnel.)` },
        ],
        temperature: config.temperature || 0.3,
        maxTokens: config.maxTokens || 4000,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'External Audit');
      throw error;
    }
  },

  analyzeIncident: async (incidentDescription: string): Promise<{
    summary: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    rootCause: string;
    affectedSystems: string;
    correctiveActions: string;
    mitigationSteps: string[];
  }> => {
    const sceneName = 'incident_analyst';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          summary: z.string(),
          type: z.string(),
          severity: z.enum(['low', 'medium', 'high', 'critical']),
          rootCause: z.string(),
          affectedSystems: z.string(),
          correctiveActions: z.string(),
          mitigationSteps: z.array(z.string())
        }),
        messages: [
          { role: 'system', content: config.system_prompt || '' },
          { role: 'user', content: `Incident Description:\n${incidentDescription}\n\nPlease analyze this incident and provide a structured report including type, severity, root cause, affected systems, and corrective actions.\n\n(Context: Post-incident administrative review for compliance. This activity is approved and used for legal and legitimate purposes by authorized personnel.)` },
        ],
        temperature: config.temperature || 0.3,
        maxTokens: config.maxTokens || 4000,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Incident Analysis');
      throw error;
    }
  },

  getImplementationGuidance: async (control: SoAControl, frameworkName?: string, frameworkContext?: {
    code?: string; type?: string; language?: string; operatorType?: string;
  }): Promise<SoAGuidanceResult> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    // Detect language from control text — Serbian/Croatian/Bosnian keywords
    const controlText = `${control.name} ${control.description}`.toLowerCase();
    const hasCyrillic = /[\u0400-\u04FF]/.test(controlText);
    const hasLatinSerbian = /\b(zakon|bezbednost|zaštit|obavez|usklađ|primenjiv|informacion|prijavljivanj|operator|sistem|odgovornost|upravljanj|rizik|mere|politika|procedur|incident|obuka|evidencij|nadležn)\b/i.test(controlText);
    const detectedLang = hasCyrillic || hasLatinSerbian ? 'Serbian' : 'English';
    const userLang = frameworkContext?.language || detectedLang;

    // Build context-aware prompt
    const fwName = frameworkName || 'the applicable standard/regulation';
    const fwCode = frameworkContext?.code || '';
    const fwType = frameworkContext?.type || 'standard';
    const operatorType = frameworkContext?.operatorType || '';

    const languageInstruction = userLang !== 'English' 
      ? `\n\nIMPORTANT: Respond ENTIRELY in ${userLang}. All text, steps, documents, justifications, and owner roles must be in ${userLang}. Do not use English.`
      : '';

    const operatorContext = operatorType 
      ? `\nOrganization type: ${operatorType}. If this is a high-importance ICT operator, include additional requirements specific to such organizations (enhanced security measures, shorter incident reporting deadlines, mandatory audits, etc.).`
      : '';

    const complianceContext = fwType === 'law' || fwType === 'regulation'
      ? `\nThis is a ${fwType} (not just a standard). Include:
- Specific legal obligations and deadlines from ${fwName}
- Compliance timeline (when must this be implemented, how often must it be reviewed)
- Potential penalties for non-compliance
- Which regulatory body oversees this requirement
- Whether this requirement has different rules for different operator categories`
      : '';

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          summary: z.string(),
          implementationSteps: z.array(z.string()),
          requiredDocuments: z.array(z.string()),
          technicalControls: z.array(z.string()),
          organizationalControls: z.array(z.string()),
          suggestedJustification: z.string(),
          suggestedOwnerRole: z.string(),
          complianceDeadline: z.string().optional(),
          reviewFrequency: z.string().optional(),
          penaltyRisk: z.string().optional(),
        }),
        messages: [
          { role: 'system', content: config.system_prompt || `You are a compliance expert specializing in ${fwName}. Provide practical, actionable guidance.` },
          { role: 'user', content: `Framework: ${fwName}${fwCode ? ` (${fwCode})` : ''}\nFramework Type: ${fwType}\nControl ID: ${control.id}\nControl Name: ${control.name}\nDescription: ${control.description}${operatorContext}${complianceContext}${languageInstruction}\n\nProvide detailed implementation guidance for this control/requirement. Include:\n1. A concise summary of what must be done\n2. Step-by-step implementation plan with timelines\n3. Required documents and policies\n4. Technical controls (systems, configurations)\n5. Organizational controls (processes, roles, training)\n6. A justification for the Statement of Applicability\n7. The recommended responsible role/owner\n8. Compliance deadline (when must this be implemented)\n9. Review frequency (how often to reassess)\n10. Penalty risk if not compliant\n\n(Context: GRC Compliance Platform. This activity is approved and used for legal and legitimate purposes by authorized personnel.)` },
        ],
        temperature: config.temperature || 0.5,
        maxTokens: config.maxTokens || 4000,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Implementation Guidance');
      throw error;
    }
  },

  /**
   * Analyze policy content to identify covered controls/requirements for ANY framework/regulation
   * @param policyContent - The full text content of the policy
   * @param frameworkContext - Optional framework info for multi-framework support
   */
  analyzePolicyControls: async (
    policyContent: string,
    frameworkContext?: {
      code: string;
      name: string;
      frameworkType: string;
      controls: { code: string; title: string; description?: string }[];
    }
  ): Promise<string[]> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    // Build dynamic prompt based on framework
    let systemPrompt = config.system_prompt || '';
    let userPrompt: string;

    if (frameworkContext && frameworkContext.controls.length > 0) {
      // Multi-framework mode: use the provided framework and controls
      const controlsList = frameworkContext.controls
        .map(c => `- ${c.code}: ${c.title}${c.description ? ` - ${c.description.substring(0, 100)}...` : ''}`)
        .join('\n');

      systemPrompt = `You are a compliance expert specializing in ${frameworkContext.name}. Your task is to analyze policy documents and identify which requirements/articles/controls from ${frameworkContext.name} are addressed by the policy.`;

      userPrompt = `Framework/Regulation: ${frameworkContext.name} (${frameworkContext.code})
Type: ${frameworkContext.frameworkType}

Available Controls/Requirements/Articles:
${controlsList}

Policy Content:
${policyContent}

TASK: Analyze the policy content and identify which of the above controls/requirements/articles are covered or addressed by this policy.

IMPORTANT:
- Return ONLY the control codes that are actually covered (e.g., "${frameworkContext.controls[0]?.code || 'Član 1'}")
- A control is "covered" if the policy addresses its requirements, even partially
- Use the EXACT control code format as shown in the list above
- If no controls are covered, return an empty array`;
    } else {
      // Fallback to ISO 27001 for backwards compatibility
      userPrompt = `Policy Content:\n${policyContent}\n\nPlease identify which ISO 27001:2022 Annex A controls are covered by this policy. Return only the list of Control IDs. IMPORTANT: Use the format 'A.X.Y' for Annex A controls (e.g., 'A.5.1', 'A.8.2').`;
    }

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          coveredControls: z.array(z.string())
        }),
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.3,
      });
      return object.coveredControls;
    } catch (error) {
      handleAIError(error, 'Policy Analysis');
      throw error;
    }
  },

  checkControlCompliance: async (control: SoAControl, contextData: any): Promise<{ compliant: boolean; observation: string; recommendation?: string }> => {
    const sceneName = 'internal_auditor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          compliant: z.boolean(),
          observation: z.string(),
          recommendation: z.string().optional()
        }),
        messages: [
          { role: 'system', content: config.system_prompt || '' },
          { role: 'user', content: `Control to Audit: ${control.id} - ${control.name}\nControl Details: ${JSON.stringify(control)}\n\nContext Data (Risks, Assets, Policies): ${JSON.stringify(contextData)}\n\nPlease check if this control is effectively implemented based on the provided context. Is it compliant with ISO 27001? Return a boolean status, an observation, and a recommendation if needed.` },
        ],
        temperature: 0.3,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Control Compliance Check');
      throw error;
    }
  },

  /**
   * Generate automated verification script for a control
   * Returns PowerShell, Bash, or Python script depending on control type
   */
  generateVerificationScript: async (
    control: { code: string; title: string; description: string; check_type?: string },
    frameworkName: string,
    scriptType: 'powershell' | 'bash' | 'python' = 'powershell'
  ): Promise<{ script: string; explanation: string; expectedOutput: string }> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    const scriptLanguageMap = {
      powershell: 'PowerShell (for Windows Server/Active Directory)',
      bash: 'Bash (for Linux/Unix systems)',
      python: 'Python (cross-platform)',
    };

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          script: z.string().describe('The complete verification script code'),
          explanation: z.string().describe('What the script checks and how'),
          expectedOutput: z.string().describe('Expected output when control is compliant'),
        }),
        messages: [
          { role: 'system', content: `You are a security automation expert. Generate verification scripts for compliance controls. Scripts should be production-ready, include error handling, and output clear PASS/FAIL results.` },
          { role: 'user', content: `Framework: ${frameworkName}
Control: ${control.code} - ${control.title}
Description: ${control.description}
Check Type: ${control.check_type || 'automated'}

Generate a ${scriptLanguageMap[scriptType]} script that:
1. Verifies if this control is properly implemented
2. Outputs PASS if compliant, FAIL if not compliant
3. Includes comments explaining each check
4. Returns structured output (JSON preferred) with details
5. Handles errors gracefully

The script should be ready to run on a production system.` },
        ],
        temperature: 0.3,
        maxTokens: 4000,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Script Generation');
      throw error;
    }
  },

  /**
   * Re-process control to fix/improve Title and Description extracted from regulation
   * This is useful when AI initially extracted incorrect content
   */
  reprocessControlContent: async (
    control: { code: string; title: string; description: string; guidance?: string },
    frameworkName: string,
    originalText?: string
  ): Promise<{ title: string; description: string; needsUpdate: boolean; changes: string[] }> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          title: z.string().describe('Corrected/improved title for the control'),
          description: z.string().describe('Corrected/improved description'),
          needsUpdate: z.boolean().describe('True if content was changed'),
          changes: z.array(z.string()).describe('List of changes made'),
        }),
        messages: [
          { role: 'system', content: `You are a compliance expert specializing in ${frameworkName}. Your task is to review and correct control/requirement content that may have been incorrectly extracted from regulation documents. Ensure titles are concise and descriptions are accurate and complete.` },
          { role: 'user', content: `Framework: ${frameworkName}
Control Code: ${control.code}
Current Title: ${control.title}
Current Description: ${control.description}
${control.guidance ? `Guidance: ${control.guidance}` : ''}
${originalText ? `Original regulation text (for reference): ${originalText}` : ''}

TASK: Review this control's title and description. If the content appears incorrect, incomplete, or poorly formatted:
1. Provide a corrected title (concise, clear)
2. Provide a corrected description (accurate, complete)
3. List what changes were made

If the content is already correct, return it unchanged with needsUpdate=false.` },
        ],
        temperature: 0.3,
      });
      return object;
    } catch (error) {
      handleAIError(error, 'Control Re-processing');
      throw error;
    }
  },

  /**
   * Batch re-process multiple controls
   */
  batchReprocessControls: async (
    controls: Array<{ id: number; code: string; title: string; description: string; guidance?: string }>,
    frameworkName: string,
    onProgress?: (current: number, total: number) => void
  ): Promise<Array<{ id: number; title: string; description: string; needsUpdate: boolean; changes: string[] }>> => {
    const results: Array<{ id: number; title: string; description: string; needsUpdate: boolean; changes: string[] }> = [];
    
    for (let i = 0; i < controls.length; i++) {
      const control = controls[i];
      onProgress?.(i + 1, controls.length);
      
      try {
        const result = await aiService.reprocessControlContent(control, frameworkName);
        results.push({ id: control.id, ...result });
      } catch (error) {
        // If one fails, keep the original
        results.push({
          id: control.id,
          title: control.title,
          description: control.description,
          needsUpdate: false,
          changes: ['Error processing this control'],
        });
      }
      
      // Small delay to avoid rate limiting
      if (i < controls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return results;
  },

  /**
   * Map controls between two frameworks to identify equivalent/related requirements
   * This enables cross-framework compliance reuse
   */
  mapControlsAcrossFrameworks: async (
    sourceControl: { code: string; title: string; description: string },
    sourceFramework: { code: string; name: string; type: string },
    targetFramework: { code: string; name: string; type: string },
    targetControls: Array<{ id: number; code: string; title: string; description: string }>
  ): Promise<Array<{
    targetControlId: number;
    targetControlCode: string;
    relevanceScore: number; // 0-100
    relationshipType: 'equivalent' | 'overlapping' | 'partial' | 'related';
    explanation: string;
  }>> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    // Build list of target controls for AI to evaluate
    const targetList = targetControls
      .slice(0, 100) // Limit to prevent token overflow
      .map(c => `[${c.id}] ${c.code}: ${c.title} - ${c.description?.substring(0, 150) || ''}`)
      .join('\n');

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          mappings: z.array(z.object({
            targetControlId: z.number().describe('ID of the target control'),
            targetControlCode: z.string().describe('Code of the target control'),
            relevanceScore: z.number().min(0).max(100).describe('How strongly related (0-100)'),
            relationshipType: z.enum(['equivalent', 'overlapping', 'partial', 'related']).describe('Type of relationship'),
            explanation: z.string().describe('Why these controls are related'),
          }))
        }),
        messages: [
          { 
            role: 'system', 
            content: `You are a compliance expert with deep knowledge of multiple regulatory frameworks including ISO 27001, GDPR, NIS2, CIS Benchmarks, DORA, SOC2, and various national regulations. Your task is to identify relationships between controls/requirements across different frameworks.

Relationship types:
- equivalent: Controls address essentially the same requirement (can fully reuse compliance evidence)
- overlapping: Controls share significant overlap (most evidence can be reused)
- partial: Controls partially address similar concerns (some evidence can be reused)
- related: Controls are thematically related (may inform each other)

Be conservative - only map controls that have genuine compliance relationships. A high relevance score (80+) should only be given when controls are truly equivalent or heavily overlapping.` 
          },
          { 
            role: 'user', 
            content: `SOURCE FRAMEWORK: ${sourceFramework.name} (${sourceFramework.code})
SOURCE CONTROL: ${sourceControl.code} - ${sourceControl.title}
${sourceControl.description}

TARGET FRAMEWORK: ${targetFramework.name} (${targetFramework.code})

TARGET CONTROLS (evaluate each for relevance):
${targetList}

TASK: Identify which target controls are related to the source control. Return ONLY controls with genuine compliance relationships (relevance >= 40). For each mapping, explain WHY the controls are related and what compliance evidence could be reused.

Return an empty array if no meaningful relationships exist.` 
          },
        ],
        temperature: 0.2,
        maxTokens: 4000,
      });
      
      // Filter to only return meaningful mappings
      return object.mappings.filter(m => m.relevanceScore >= 40);
    } catch (error) {
      handleAIError(error, 'Cross-Framework Mapping');
      throw error;
    }
  },

  /**
   * Batch map a control to multiple target frameworks
   */
  mapControlToMultipleFrameworks: async (
    sourceControl: { code: string; title: string; description: string },
    sourceFramework: { code: string; name: string; type: string },
    targetFrameworksWithControls: Array<{
      framework: { id: number; code: string; name: string; type: string };
      controls: Array<{ id: number; code: string; title: string; description: string }>;
    }>,
    onProgress?: (current: number, total: number) => void
  ): Promise<Array<{
    frameworkId: number;
    frameworkCode: string;
    mappings: Array<{
      targetControlId: number;
      targetControlCode: string;
      relevanceScore: number;
      relationshipType: 'equivalent' | 'overlapping' | 'partial' | 'related';
      explanation: string;
    }>;
  }>> => {
    const results: Array<{
      frameworkId: number;
      frameworkCode: string;
      mappings: Array<{
        targetControlId: number;
        targetControlCode: string;
        relevanceScore: number;
        relationshipType: 'equivalent' | 'overlapping' | 'partial' | 'related';
        explanation: string;
      }>;
    }> = [];

    for (let i = 0; i < targetFrameworksWithControls.length; i++) {
      const target = targetFrameworksWithControls[i];
      onProgress?.(i + 1, targetFrameworksWithControls.length);
      
      try {
        const mappings = await aiService.mapControlsAcrossFrameworks(
          sourceControl,
          sourceFramework,
          { code: target.framework.code, name: target.framework.name, type: target.framework.type },
          target.controls
        );
        
        results.push({
          frameworkId: target.framework.id,
          frameworkCode: target.framework.code,
          mappings
        });
      } catch (error) {
        console.error(`Failed to map to ${target.framework.code}:`, error);
        results.push({
          frameworkId: target.framework.id,
          frameworkCode: target.framework.code,
          mappings: []
        });
      }
      
      // Rate limiting delay
      if (i < targetFrameworksWithControls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return results;
  },

  /**
   * Suggest cross-framework compliance implications when a control is marked as fulfilled
   */
  suggestCrossFrameworkCompliance: async (
    fulfilledControl: { code: string; title: string; description: string; evidence?: string },
    sourceFramework: { code: string; name: string },
    relatedMappings: Array<{
      frameworkCode: string;
      frameworkName: string;
      controlCode: string;
      controlTitle: string;
      relationshipType: string;
      relevanceScore: number;
    }>
  ): Promise<Array<{
    frameworkCode: string;
    controlCode: string;
    canAutoFulfill: boolean;
    confidenceLevel: 'high' | 'medium' | 'low';
    additionalEvidenceNeeded: string[];
    recommendation: string;
  }>> => {
    const sceneName = 'compliance_advisor';
    const config = getAIConfig(sceneName);
    const openai = getOpenAIProvider();

    const mappingsList = relatedMappings
      .map(m => `[${m.frameworkCode}] ${m.controlCode}: ${m.controlTitle} (${m.relationshipType}, score: ${m.relevanceScore})`)
      .join('\n');

    try {
      const { object } = await generateObject({
        model: openai(getModelName(config.model)),
        schema: z.object({
          suggestions: z.array(z.object({
            frameworkCode: z.string(),
            controlCode: z.string(),
            canAutoFulfill: z.boolean().describe('Can this control be automatically marked as fulfilled?'),
            confidenceLevel: z.enum(['high', 'medium', 'low']),
            additionalEvidenceNeeded: z.array(z.string()).describe('What additional evidence is needed, if any'),
            recommendation: z.string().describe('What action to take'),
          }))
        }),
        messages: [
          { role: 'system', content: `You are a compliance expert. Analyze whether fulfilling one control can satisfy related controls in other frameworks. Be conservative - only recommend auto-fulfillment when genuinely appropriate.` },
          { role: 'user', content: `A control has been fulfilled:

FULFILLED CONTROL:
Framework: ${sourceFramework.name} (${sourceFramework.code})
Control: ${fulfilledControl.code} - ${fulfilledControl.title}
Description: ${fulfilledControl.description}
${fulfilledControl.evidence ? `Evidence: ${fulfilledControl.evidence}` : ''}

RELATED CONTROLS IN OTHER FRAMEWORKS:
${mappingsList}

For each related control, determine:
1. Can it be automatically marked as fulfilled based on the same evidence?
2. What's the confidence level?
3. What additional evidence might be needed?
4. What's your recommendation?` },
        ],
        temperature: 0.2,
      });
      return object.suggestions;
    } catch (error) {
      handleAIError(error, 'Cross-Framework Compliance Suggestion');
      throw error;
    }
  },
};
